import { Anchor, Container, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { FORMSHIVE_EVENT_TYPES } from '@/components/Integrations/adapters/registry';
import { IntegrationDeliveryHistory } from '@/components/Integrations/IntegrationDeliveryHistory';
import type { DeliveryFilters, DeliveryRecord } from '@/components/Integrations/types';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { PageHeader } from '../../../components/Common/PageHeader';
import { useRustyState } from '../../../state';

const PAGE_SIZE = 50;

/**
 * Replay history page for a single integration.
 *
 * Pulls deliveries from `GET /v1/a/integrations/{id}/deliveries`. The "Resend"
 * action POSTs `/v1/a/integrations/deliveries/{delivery_id}/replay`. The
 * backend re-enqueues an outbox row, so the response is acknowledged async —
 * we just refresh the list so the user can watch the new attempt land.
 */
export function AccountIntegrationDeliveriesPage() {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const { uuid } = useParams<{ uuid: string }>();
  const { createLanguageURL } = useLanguageAwareRouting();

  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<DeliveryFilters>({});
  const [loading, setLoading] = useState(false);

  const eventTypeOptions = useMemo(
    () =>
      FORMSHIVE_EVENT_TYPES.map((e) => ({
        value: e.type,
        label: t(e.label),
      })),
    [t]
  );

  const load = useCallback(async () => {
    if (!uuid) return;
    setLoading(true);
    try {
      const res = await api.listIntegrationDeliveries(uuid, {
        page: page - 1, // backend uses 0-based
        page_size: PAGE_SIZE,
        outcome: filters.outcome ? filters.outcome : undefined,
        event_type: filters.event_type || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
      });
      setDeliveries(res.data as DeliveryRecord[]);
      setTotal(res.total);
    } catch (e) {
      console.error('Failed to load deliveries', e);
    } finally {
      setLoading(false);
    }
  }, [api, uuid, page, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleResend = useCallback(
    async (deliveryId: string) => {
      try {
        await api.replayIntegrationDelivery(deliveryId);
        notifications.show({
          color: 'blue',
          title: t('formshive.integrations.replay.queuedTitle'),
          message: t('formshive.integrations.replay.queuedBody'),
        });
        // Give the worker a moment, then refresh so the user sees the new
        // attempt row.
        setTimeout(() => load(), 800);
      } catch (e) {
        console.error('Failed to replay delivery', e);
      }
    },
    [api, t, load]
  );

  return (
    <Container fluid>
      <Stack gap="md">
        <Anchor
          component={Link}
          to={createLanguageURL(`/account/integrations/${uuid}`)}
          size="sm"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start' }}
        >
          <IconArrowLeft size={14} />
          {t('formshive.integrations.deliveriesPage.back')}
        </Anchor>
        <PageHeader
          title={t('formshive.integrations.deliveriesPage.title')}
          subtitle={t('formshive.integrations.deliveriesPage.subtitle')}
        />
        <IntegrationDeliveryHistory
          deliveries={deliveries}
          loading={loading}
          totalCount={total}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          filters={filters}
          onFilterChange={(f) => {
            setPage(1);
            setFilters(f);
          }}
          onResend={handleResend}
          eventTypeOptions={eventTypeOptions}
        />
      </Stack>
    </Container>
  );
}
